import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, StorageKey } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Variable, ModuleFE_Value, ModuleFE_Expression, DropDown_VariableV3, DropDown_Values } from '@app/hcs-frontend';
import { WorkHubItem_Expression, UtilObject_Expression } from '@app/hcs-frontend/_entity/expression/util-object';
import { resolveValueDisplay } from '@app/hcs-shared';
import { ModuleFE_DiseaseProfile, ModuleFE_MultiVar, ModuleFE_DiseaseView } from '@app/dp-frontend';
import { WorkHubItem_DiseaseProfile, UtilObject_DiseaseProfile } from '@app/dp-frontend/search/index';
import { Manager_DiseaseProfile_MultiVarTab_Values, Manager_DiseaseProfile_ProfileTab_General } from '@app/dp-frontend/ui/_managers/Manager_DiseaseProfile/types';
import { ModuleFE_Pathway } from '@app/pathway-frontend';
import { WorkHubItem_Pathway, UtilObject_Pathway } from '@app/pathway-frontend/search/items/pathway';
import { ModuleFE_DataMigration } from './ModuleFE_DataMigration.js';
import './Page_Migration.scss';
const StorageKey_MigrationRules = new StorageKey('hv-migration__rules');
let _cachedDryRun;
let _cachedExcludedIds = [];
const _entityWorkHubActions = new Map();
const DP_COLLECTIONS = new Set(['disease-profiles', 'multi-vars', 'disease-values']);
const PW_COLLECTIONS = new Set(['pathway', 'pathway-states', 'pathway-transitions', 'orders']);
export class Page_Migration extends ComponentSync {
    static Route = Object.freeze({
        key: 'hv-migration',
        path: 'hv-migration',
        Component: Page_Migration,
        modulesToAwait: [ModuleFE_Variable, ModuleFE_Value],
    });
    _treeCache;
    _initialized = false;
    deriveStateFromProps(nextProps, state) {
        state.rules ??= StorageKey_MigrationRules.get([]);
        state.editor ??= {};
        state.loadingAction ??= undefined;
        state.dryRun ??= _cachedDryRun;
        state.excludedEntityIds ??= new Set(_cachedExcludedIds);
        state.expandedDiffs ??= new Set();
        state.collapsedCollections ??= new Set();
        state.rawJsonDiffs ??= new Set();
        if (!this._initialized && state.dryRun) {
            this._initialized = true;
            this.collapseAllCollections(state);
        }
        return state;
    }
    collapseAllCollections(state) {
        if (!state.dryRun)
            return;
        const collapsed = new Set();
        collapsed.add('Disease Profiles');
        collapsed.add('Pathways');
        for (const col of state.dryRun.collections)
            collapsed.add(col.collection);
        state.collapsedCollections = collapsed;
    }
    // ─── Actions ────────────────────────────────────────────────────
    canAddRule(editor) {
        if (!editor.sourceVarId || !editor.targetVarId)
            return { allowed: false };
        const hasAnyValue = !!(editor.sourceValId || editor.targetValId);
        const hasBothValues = !!(editor.sourceValId && editor.targetValId);
        if (hasAnyValue && !hasBothValues)
            return { allowed: false, reason: 'Both sides must specify a value, or neither' };
        if (!hasAnyValue) {
            const sourceVar = ModuleFE_Variable.cache.unique(editor.sourceVarId);
            const targetVar = ModuleFE_Variable.cache.unique(editor.targetVarId);
            if (sourceVar && targetVar && sourceVar.valueId !== targetVar.valueId)
                return { allowed: false, reason: 'Value types must match for variable-only rules' };
        }
        return { allowed: true };
    }
    addRule = () => {
        const { editor, rules } = this.state;
        if (!this.canAddRule(editor).allowed)
            return;
        rules.push({
            sourceVarId: editor.sourceVarId,
            sourceValId: editor.sourceValId,
            targetVarId: editor.targetVarId,
            targetValId: editor.targetValId,
        });
        StorageKey_MigrationRules.set(rules);
        this.clearPersistedDryRun();
        this.setState({ rules, editor: {}, dryRun: undefined, result: undefined, excludedEntityIds: new Set() });
    };
    removeRule = (index) => {
        const rules = [...this.state.rules];
        rules.splice(index, 1);
        StorageKey_MigrationRules.set(rules);
        this.clearPersistedDryRun();
        this.setState({ rules, dryRun: undefined, result: undefined, excludedEntityIds: new Set() });
    };
    clearAll = () => {
        StorageKey_MigrationRules.set([]);
        this.clearPersistedDryRun();
        this.setState({ rules: [], editor: {}, dryRun: undefined, result: undefined, excludedEntityIds: new Set(), expandedDiffs: new Set() });
    };
    clearPersistedDryRun = () => {
        _cachedDryRun = undefined;
        _cachedExcludedIds = [];
    };
    runDryRun = async () => {
        if (!this.state.rules.length)
            return;
        this.setState({ loadingAction: 'dryRun', error: undefined });
        try {
            const response = await ModuleFE_DataMigration._v1.dryRun({ plan: { rules: this.state.rules } }).executeSync();
            _cachedDryRun = response.dryRun;
            _cachedExcludedIds = [];
            const collapsedCollections = new Set();
            collapsedCollections.add('Disease Profiles');
            collapsedCollections.add('Pathways');
            for (const col of response.dryRun.collections)
                collapsedCollections.add(col.collection);
            this.setState({ dryRun: response.dryRun, loadingAction: undefined, excludedEntityIds: new Set(), expandedDiffs: new Set(), collapsedCollections });
        }
        catch (e) {
            this.setState({ error: e.message ?? 'Dry run failed', loadingAction: undefined });
        }
    };
    runExecute = async () => {
        if (!this.state.rules.length)
            return;
        const selectedCount = this.getSelectedCount();
        if (!confirm(`This will permanently rewrite ${selectedCount} entities across multiple collections. Continue?`))
            return;
        this.setState({ loadingAction: 'execute', error: undefined });
        const excludeEntityIds = [...this.state.excludedEntityIds];
        try {
            const response = await ModuleFE_DataMigration._v1.execute({
                plan: { rules: this.state.rules },
                excludeEntityIds: excludeEntityIds.length ? excludeEntityIds : undefined,
            }).executeSync();
            this.clearPersistedDryRun();
            this.setState({ result: response.result, dryRun: undefined, loadingAction: undefined, excludedEntityIds: new Set() });
        }
        catch (e) {
            this.setState({ error: e.message ?? 'Execution failed', loadingAction: undefined });
        }
    };
    // ─── Selection helpers ──────────────────────────────────────────
    toggleEntity = (entityId) => {
        const excluded = new Set(this.state.excludedEntityIds);
        if (excluded.has(entityId))
            excluded.delete(entityId);
        else
            excluded.add(entityId);
        _cachedExcludedIds = [...excluded];
        this.setState({ excludedEntityIds: excluded });
    };
    toggleCollection = (diffs) => {
        const excluded = new Set(this.state.excludedEntityIds);
        const allExcluded = diffs.every(d => excluded.has(d._id));
        for (const d of diffs) {
            if (allExcluded)
                excluded.delete(d._id);
            else
                excluded.add(d._id);
        }
        _cachedExcludedIds = [...excluded];
        this.setState({ excludedEntityIds: excluded });
    };
    getSelectedCount() {
        if (!this.state.dryRun)
            return 0;
        let total = 0;
        for (const c of this.state.dryRun.collections)
            for (const d of c.diffs)
                if (!this.state.excludedEntityIds.has(d._id))
                    total++;
        return total;
    }
    // ─── Helpers ────────────────────────────────────────────────────
    resolveVarName = (id) => {
        if (!id)
            return '';
        return ModuleFE_Variable.cache.unique(id)?.name ?? id;
    };
    resolveValName = (id) => {
        if (!id)
            return '';
        const val = ModuleFE_Value.cache.unique(id);
        if (!val)
            return id;
        return resolveValueDisplay(val);
    };
    downloadJson = (data, filename) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    resolveEntityLabel = (diff) => {
        const entity = diff.before;
        if (entity?.name)
            return entity.name;
        if (entity?.label)
            return entity.label;
        if (entity?.title)
            return entity.title;
        if (diff.collection === 'disease-values' || diff.collection === 'multi-vars') {
            const dpId = entity?._diseaseProfileId ?? entity?.diseaseProfileId;
            if (dpId) {
                const varIds = entity?.variables
                    ? entity.variables.map((v) => v.variableId).filter(Boolean)
                    : Object.keys(entity?.values ?? {});
                const varNames = varIds.map((id) => ModuleFE_Variable.cache.unique(id)?.name).filter(Boolean);
                const label = varNames.length ? varNames.join(', ') : diff._id.slice(0, 8);
                return `${label} (${diff._id.slice(0, 6)})`;
            }
        }
        return diff._id.slice(0, 12);
    };
    toggleDiffExpand = (id) => {
        const expanded = new Set(this.state.expandedDiffs);
        if (expanded.has(id))
            expanded.delete(id);
        else
            expanded.add(id);
        this.setState({ expandedDiffs: expanded });
    };
    toggleCollectionCollapse = (collection) => {
        const collapsed = new Set(this.state.collapsedCollections);
        if (collapsed.has(collection))
            collapsed.delete(collection);
        else
            collapsed.add(collection);
        this.setState({ collapsedCollections: collapsed });
    };
    toggleRawJson = (id) => {
        const raw = new Set(this.state.rawJsonDiffs);
        if (raw.has(id))
            raw.delete(id);
        else
            raw.add(id);
        this.setState({ rawJsonDiffs: raw });
    };
    resolveIdToLabel = (id) => {
        const variable = ModuleFE_Variable.cache.unique(id);
        if (variable)
            return variable.name;
        const value = ModuleFE_Value.cache.unique(id);
        if (value)
            return resolveValueDisplay(value);
        return undefined;
    };
    formatLeafValue = (val) => {
        if (val == null)
            return '—';
        if (typeof val === 'object')
            return JSON.stringify(val);
        const str = String(val);
        const label = this.resolveIdToLabel(str);
        return label ? `${label}` : str;
    };
    formatPath = (path) => {
        return path.replace(/(?:^|\.)([a-f0-9]{32})(?=\.|$|\[)/g, (_match, id) => {
            const label = this.resolveIdToLabel(id);
            return label ? `.«${label}»` : `.${id}`;
        });
    };
    computeLeafDiffs = (before, after, path = '') => {
        const diffs = [];
        const currentKey = path.split('.').pop() ?? '';
        if (currentKey.startsWith('_'))
            return diffs;
        if (before === after)
            return diffs;
        if (before == null || after == null || typeof before !== 'object' || typeof after !== 'object') {
            diffs.push({ path: path || '(root)', before, after });
            return diffs;
        }
        if (Array.isArray(before) && Array.isArray(after)) {
            const maxLen = Math.max(before.length, after.length);
            for (let i = 0; i < maxLen; i++)
                diffs.push(...this.computeLeafDiffs(before[i], after[i], `${path}[${i}]`));
            return diffs;
        }
        const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
        for (const key of allKeys) {
            if (key.startsWith('_'))
                continue;
            diffs.push(...this.computeLeafDiffs(before[key], after[key], path ? `${path}.${key}` : key));
        }
        return diffs;
    };
    // ─── Tree builder ────────────────────────────────────────────────
    buildDryRunTree = (dryRun) => {
        _entityWorkHubActions.clear();
        const dpDiffs = new Map();
        const mvDiffs = new Map();
        const dvDiffs = new Map();
        const pwDiffs = new Map();
        const psDiffs = new Map();
        const ptDiffs = new Map();
        const orderDiffs = new Map();
        const flatNodes = [];
        for (const col of dryRun.collections) {
            switch (col.collection) {
                case 'disease-profiles':
                    for (const d of col.diffs)
                        dpDiffs.set(d._id, [...(dpDiffs.get(d._id) ?? []), d]);
                    break;
                case 'multi-vars':
                    for (const d of col.diffs) {
                        const dpId = d.before?.diseaseProfileId;
                        if (dpId)
                            mvDiffs.set(dpId, [...(mvDiffs.get(dpId) ?? []), d]);
                    }
                    break;
                case 'disease-values':
                    for (const d of col.diffs) {
                        const mvId = d.before?.multiVarId;
                        if (mvId)
                            dvDiffs.set(mvId, [...(dvDiffs.get(mvId) ?? []), d]);
                    }
                    break;
                case 'pathway':
                    for (const d of col.diffs)
                        pwDiffs.set(d._id, [...(pwDiffs.get(d._id) ?? []), d]);
                    break;
                case 'pathway-states':
                    for (const d of col.diffs) {
                        const pwId = d.before?.pathwayIds?.[0];
                        if (pwId)
                            psDiffs.set(pwId, [...(psDiffs.get(pwId) ?? []), d]);
                    }
                    break;
                case 'pathway-transitions':
                    for (const d of col.diffs) {
                        const pwId = d.before?.pathwayIds?.[0];
                        if (pwId)
                            ptDiffs.set(pwId, [...(ptDiffs.get(pwId) ?? []), d]);
                    }
                    break;
                case 'orders':
                    for (const d of col.diffs) {
                        const pwId = d.before?.pathwayIds?.[0];
                        if (pwId)
                            orderDiffs.set(pwId, [...(orderDiffs.get(pwId) ?? []), d]);
                    }
                    break;
                case 'expressions':
                    if (col.diffs.length) {
                        for (const d of col.diffs)
                            _entityWorkHubActions.set(d._id, () => this.openExpressionInWorkHub(d._id, d.before?.label));
                        flatNodes.push({ kind: 'collection', collection: col.collection, children: col.diffs.map(d => ({ kind: 'entity', diff: d })), allDiffs: col.diffs });
                    }
                    break;
                default:
                    if (col.diffs.length)
                        flatNodes.push({ kind: 'collection', collection: col.collection, children: col.diffs.map(d => ({ kind: 'entity', diff: d })), allDiffs: col.diffs });
                    break;
            }
        }
        const treeNodes = [];
        const allDpIds = new Set();
        for (const id of dpDiffs.keys())
            allDpIds.add(id);
        for (const col of dryRun.collections) {
            if (col.collection === 'multi-vars')
                for (const d of col.diffs) {
                    const dpId = d.before?.diseaseProfileId;
                    if (dpId)
                        allDpIds.add(dpId);
                }
            if (col.collection === 'disease-values')
                for (const d of col.diffs) {
                    const dpId = d.before?._diseaseProfileId;
                    if (dpId)
                        allDpIds.add(dpId);
                }
        }
        const dpAllDiffs = [];
        for (const dpId of allDpIds) {
            const dp = ModuleFE_DiseaseProfile.cache.unique(dpId);
            const dpLabel = dp?.label ?? dpId.slice(0, 12);
            const childrenOfDp = [];
            const dpEntityDiffs = dpDiffs.get(dpId) ?? [];
            for (const d of dpEntityDiffs) {
                childrenOfDp.push({ kind: 'entity', diff: d });
                dpAllDiffs.push(d);
            }
            const mvDiffsForDp = mvDiffs.get(dpId) ?? [];
            const allMvIds = new Set();
            for (const d of mvDiffsForDp)
                allMvIds.add(d._id);
            for (const col of dryRun.collections) {
                if (col.collection === 'disease-values')
                    for (const d of col.diffs) {
                        const mvId = d.before?.multiVarId;
                        const dDpId = d.before?._diseaseProfileId;
                        if (mvId && dDpId === dpId)
                            allMvIds.add(mvId);
                    }
            }
            for (const mvId of allMvIds) {
                const mv = ModuleFE_MultiVar.cache.unique(mvId);
                const varIds = mv?.variables?.map((v) => v.variableId).filter(Boolean) ?? [];
                const varNames = varIds.map(id => ModuleFE_Variable.cache.unique(id)?.name).filter(Boolean);
                const mvLabel = varNames.length ? varNames.join(', ') : mvId.slice(0, 12);
                const mvEntityDiffs = [];
                const mvEntityDiff = mvDiffsForDp.find(d => d._id === mvId);
                if (mvEntityDiff)
                    mvEntityDiffs.push(mvEntityDiff);
                const dvsForMv = dvDiffs.get(mvId) ?? [];
                const allMvEntityDiffs = [...mvEntityDiffs, ...dvsForMv];
                for (const d of allMvEntityDiffs)
                    dpAllDiffs.push(d);
                if (!allMvEntityDiffs.length)
                    continue;
                const openMvInWorkHub = () => this.openDiseaseProfileInWorkHub(dpId, dpLabel, mvId);
                for (const d of allMvEntityDiffs)
                    _entityWorkHubActions.set(d._id, openMvInWorkHub);
                if (allMvEntityDiffs.length === 1) {
                    childrenOfDp.push({ kind: 'entity', diff: allMvEntityDiffs[0] });
                }
                else {
                    childrenOfDp.push({
                        kind: 'group',
                        id: mvId,
                        label: mvLabel,
                        collection: 'multi-vars',
                        openInWorkHub: openMvInWorkHub,
                        children: allMvEntityDiffs.map(d => ({ kind: 'entity', diff: d })),
                    });
                }
            }
            if (childrenOfDp.length) {
                treeNodes.push({
                    kind: 'group',
                    id: dpId,
                    label: dpLabel,
                    collection: 'disease-profiles',
                    openInWorkHub: () => this.openDiseaseProfileInWorkHub(dpId, dpLabel),
                    children: childrenOfDp,
                });
            }
        }
        const allPwIds = new Set();
        for (const id of pwDiffs.keys())
            allPwIds.add(id);
        for (const id of psDiffs.keys())
            allPwIds.add(id);
        for (const id of ptDiffs.keys())
            allPwIds.add(id);
        for (const id of orderDiffs.keys())
            allPwIds.add(id);
        const pwAllDiffs = [];
        for (const pwId of allPwIds) {
            const pw = ModuleFE_Pathway.cache.unique(pwId);
            const pwLabel = pw?.label ?? pwId.slice(0, 12);
            const childrenOfPw = [];
            const pwEntityDiffs = pwDiffs.get(pwId) ?? [];
            for (const d of pwEntityDiffs) {
                _entityWorkHubActions.set(d._id, () => this.openPathwayInWorkHub(pwId, pwLabel));
                childrenOfPw.push({ kind: 'entity', diff: d });
                pwAllDiffs.push(d);
            }
            for (const d of (psDiffs.get(pwId) ?? [])) {
                _entityWorkHubActions.set(d._id, () => this.openPathwayInWorkHub(pwId, pwLabel, d._id, undefined));
                childrenOfPw.push({ kind: 'entity', diff: d });
                pwAllDiffs.push(d);
            }
            for (const d of (ptDiffs.get(pwId) ?? [])) {
                _entityWorkHubActions.set(d._id, () => this.openPathwayInWorkHub(pwId, pwLabel, undefined, d._id));
                childrenOfPw.push({ kind: 'entity', diff: d });
                pwAllDiffs.push(d);
            }
            for (const d of (orderDiffs.get(pwId) ?? [])) {
                _entityWorkHubActions.set(d._id, () => this.openPathwayInWorkHub(pwId, pwLabel));
                childrenOfPw.push({ kind: 'entity', diff: d });
                pwAllDiffs.push(d);
            }
            if (childrenOfPw.length) {
                treeNodes.push({
                    kind: 'group',
                    id: pwId,
                    label: pwLabel,
                    collection: 'pathway',
                    openInWorkHub: () => this.openPathwayInWorkHub(pwId, pwLabel),
                    children: childrenOfPw,
                });
            }
        }
        const dpCollectionNode = dpAllDiffs.length
            ? { kind: 'collection', collection: 'Disease Profiles', children: treeNodes.filter(n => n.kind === 'group' && DP_COLLECTIONS.has(n.collection)), allDiffs: dpAllDiffs }
            : undefined;
        const pwCollectionNode = pwAllDiffs.length
            ? { kind: 'collection', collection: 'Pathways', children: treeNodes.filter(n => n.kind === 'group' && PW_COLLECTIONS.has(n.collection)), allDiffs: pwAllDiffs }
            : undefined;
        const result = [];
        if (dpCollectionNode)
            result.push(dpCollectionNode);
        if (pwCollectionNode)
            result.push(pwCollectionNode);
        result.push(...flatNodes);
        return result;
    };
    // ─── WorkHub navigation ──────────────────────────────────────────
    openDiseaseProfileInWorkHub = (dpId, dpLabel, multiVarId) => {
        const views = ModuleFE_DiseaseView.cache.all();
        const selectedDiseaseViewId = views[0]?._id ?? '';
        WorkHubItem_DiseaseProfile.openTab(UtilObject_DiseaseProfile.generateTabId(dpId), dpLabel, {
            itemId: dpId,
            editMode: false,
            profile: { selectedDiseaseViewId, selectedTab: Manager_DiseaseProfile_ProfileTab_General },
            multiVar: { id: multiVarId, selectedTab: Manager_DiseaseProfile_MultiVarTab_Values },
        });
    };
    openPathwayInWorkHub = (pwId, pwLabel, stateId, transitionId) => {
        WorkHubItem_Pathway.openTab(UtilObject_Pathway.generateTabId(pwId), pwLabel, {
            itemId: pwId,
            editMode: false,
            stateId,
            transitionId,
        });
    };
    openExpressionInWorkHub = (expressionId, label) => {
        const resolvedLabel = label ?? ModuleFE_Expression.cache.unique(expressionId)?.label ?? 'Expression';
        WorkHubItem_Expression.openTab(UtilObject_Expression.generateTabId(expressionId), resolvedLabel, { itemId: expressionId, editMode: false });
    };
    // ─── Render ─────────────────────────────────────────────────────
    render() {
        const { rules, editor, dryRun, result, loadingAction, error } = this.state;
        return _jsx("div", { id: "page__hv-migration", children: _jsxs("div", { className: "migration__inner", children: [_jsxs("div", { children: [_jsx("p", { className: "migration__title", children: "Healthcare variable migration" }), _jsx("p", { className: "migration__subtitle", children: "Map source variables to target variables and migrate records" })] }), this.renderRuleEditor(editor), this.renderRulesCard(rules), this.renderActions(rules, loadingAction, dryRun), error && _jsx("div", { className: "migration__error", children: error }), dryRun && this.renderDryRun(dryRun), result && this.renderResult(result)] }) });
    }
    renderRuleEditor(editor) {
        const sourceVar = editor.sourceVarId ? ModuleFE_Variable.cache.unique(editor.sourceVarId) : undefined;
        const targetVar = editor.targetVarId ? ModuleFE_Variable.cache.unique(editor.targetVarId) : undefined;
        const { allowed, reason } = this.canAddRule(editor);
        return _jsxs("div", { className: "migration__card", children: [_jsx("div", { className: "migration__section-label", children: "Add rule" }), _jsxs("div", { className: "migration__rule-row", children: [_jsxs("div", { className: "migration__side", children: [_jsxs("div", { className: "migration__field", children: [_jsx("label", { className: "migration__field-label", children: "Source variable" }), _jsx(DropDown_VariableV3.selectable, { selected: editor.sourceVarId, onSelected: (variable) => this.setState({ editor: { ...editor, sourceVarId: variable._id, sourceValId: undefined } }), limitItems: 50 })] }), _jsxs("div", { className: `migration__field${sourceVar ? '' : ' migration__field--dimmed'}`, children: [_jsxs("label", { className: "migration__field-label", children: ["Source value ", _jsx("span", { className: "migration__optional-tag", children: "optional" })] }), sourceVar
                                            ? _jsx(DropDown_Values, { variable: sourceVar, selected: editor.sourceValId, onSelected: (value) => this.setState({ editor: { ...editor, sourceValId: value._id } }), limitItems: 50 })
                                            : _jsx("select", { disabled: true, children: _jsx("option", { children: "Select a value" }) })] })] }), _jsx("div", { className: "migration__arrow-col", children: "\u2192" }), _jsxs("div", { className: "migration__side", children: [_jsxs("div", { className: "migration__field", children: [_jsx("label", { className: "migration__field-label", children: "Target variable" }), _jsx(DropDown_VariableV3.selectable, { selected: editor.targetVarId, onSelected: (variable) => this.setState({ editor: { ...editor, targetVarId: variable._id, targetValId: undefined } }), limitItems: 50 })] }), _jsxs("div", { className: `migration__field${targetVar ? '' : ' migration__field--dimmed'}`, children: [_jsxs("label", { className: "migration__field-label", children: ["Target value ", _jsx("span", { className: "migration__optional-tag", children: "optional" })] }), targetVar
                                            ? _jsx(DropDown_Values, { variable: targetVar, selected: editor.targetValId, onSelected: (value) => this.setState({ editor: { ...editor, targetValId: value._id } }), limitItems: 50 })
                                            : _jsx("select", { disabled: true, children: _jsx("option", { children: "Select a value" }) })] })] }), _jsx("div", { className: "migration__add-col", children: _jsx("button", { className: "migration__btn-add", onClick: this.addRule, disabled: !allowed, children: "+ Add rule" }) })] }), reason && _jsx("div", { className: "migration__hint", children: reason })] });
    }
    renderRulesCard(rules) {
        return _jsxs("div", { className: "migration__card", children: [_jsxs("div", { className: "migration__section-header", children: [_jsxs("div", { className: "migration__section-label", children: ["Migration rules ", _jsx("span", { className: "migration__count", children: rules.length })] }), rules.length > 0 && _jsx("button", { className: "btn-ghost", onClick: this.clearAll, children: "Clear all" })] }), rules.length === 0
                    ? _jsxs("div", { className: "migration__empty", children: [_jsx("span", { className: "migration__empty-icon", children: "\u2922" }), "No rules yet \u2014 add one above"] })
                    : this.renderRulesTable(rules)] });
    }
    renderRulesTable(rules) {
        return _jsxs("table", { className: "migration__rules-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Source variable" }), _jsx("th", {}), _jsx("th", { children: "Target variable" }), _jsx("th", {})] }) }), _jsx("tbody", { children: rules.map((rule, i) => {
                        const srcVarName = this.resolveVarName(rule.sourceVarId);
                        const srcValName = this.resolveValName(rule.sourceValId);
                        const tgtVarName = this.resolveVarName(rule.targetVarId);
                        const tgtValName = this.resolveValName(rule.targetValId);
                        return _jsxs("tr", { children: [_jsxs("td", { children: [_jsx("div", { className: "migration__var-name", children: srcVarName }), srcValName
                                            ? _jsx("div", { className: "migration__val-name", children: srcValName })
                                            : _jsx("div", { className: "migration__muted", children: "all values" })] }), _jsx("td", { className: "migration__arrow-cell", children: "\u2192" }), _jsxs("td", { children: [_jsx("div", { className: "migration__var-name", children: tgtVarName }), tgtValName
                                            ? _jsx("div", { className: "migration__val-name", children: tgtValName })
                                            : _jsx("div", { className: "migration__muted", children: "all values" })] }), _jsx("td", { children: _jsx("button", { className: "migration__del-btn", onClick: () => this.removeRule(i), "aria-label": "Remove rule", children: "\u2715" }) })] }, i);
                    }) })] });
    }
    renderActions(rules, loadingAction, dryRun) {
        const selectedCount = this.getSelectedCount();
        const hasRules = rules.length > 0;
        const loading = !!loadingAction;
        return _jsxs("div", { className: "migration__actions", children: [_jsx("button", { className: "btn-warning", onClick: this.runDryRun, disabled: loading || !hasRules, children: loadingAction === 'dryRun' ? 'Working\u2026' : 'Dry run' }), _jsx("button", { className: "btn-primary", onClick: this.runExecute, disabled: loading || !hasRules || !dryRun || selectedCount === 0, children: loadingAction === 'execute' ? 'Executing\u2026' : `Execute migration${dryRun ? ` (${selectedCount})` : ''}` })] });
    }
    getTree(dryRun) {
        if (this._treeCache?.dryRun === dryRun)
            return this._treeCache.tree;
        const tree = this.buildDryRunTree(dryRun);
        this._treeCache = { dryRun, tree };
        return tree;
    }
    renderDryRun(dryRun) {
        const selectedCount = this.getSelectedCount();
        const tree = this.getTree(dryRun);
        return _jsxs("div", { className: "migration__card migration__dry-run", children: [_jsxs("div", { className: "migration__dry-run__header", children: [_jsxs("div", { className: "migration__dry-run__header-left", children: [_jsx("span", { className: "migration__dry-run__heading", children: "Dry run" }), _jsxs("span", { className: "migration__badge--amber", children: [selectedCount, " / ", dryRun.totalAffected, " selected"] })] }), _jsx("button", { className: "btn-ghost", onClick: () => this.downloadJson(dryRun, `hv-migration-dry-run-${Date.now()}.json`), children: "Download" })] }), _jsx("div", { className: "migration__dry-run__body", children: tree.map((node, i) => this.renderTreeNode(node, 0, `root-${i}`)) })] });
    }
    renderTreeNode(node, depth, key) {
        switch (node.kind) {
            case 'collection':
                return this.renderCollectionNode(node, key);
            case 'group':
                return this.renderGroupNode(node, depth, key);
            case 'entity':
                return this.renderEntityNode(node.diff, depth, key);
        }
    }
    renderCollectionNode(node, key) {
        const { excludedEntityIds, collapsedCollections } = this.state;
        const allExcluded = node.allDiffs.every(d => excludedEntityIds.has(d._id));
        const someExcluded = node.allDiffs.some(d => excludedEntityIds.has(d._id));
        const isCollapsed = collapsedCollections.has(node.collection);
        return _jsxs("div", { className: "migration__collection", children: [_jsxs("div", { className: "migration__collection__header", children: [_jsx("input", { type: "checkbox", checked: !allExcluded, ref: el => { if (el)
                                el.indeterminate = someExcluded && !allExcluded; }, onChange: () => this.toggleCollection(node.allDiffs), onClick: (e) => e.stopPropagation() }), _jsx("span", { className: "migration__collection__toggle", onClick: () => this.toggleCollectionCollapse(node.collection), children: isCollapsed ? '\u25B6' : '\u25BC' }), _jsx("span", { onClick: () => this.toggleCollectionCollapse(node.collection), children: node.collection }), _jsx("span", { className: "migration__badge", children: node.allDiffs.length })] }), !isCollapsed && node.children.map((child, i) => this.renderTreeNode(child, 1, `${key}-${i}`))] }, key);
    }
    renderGroupNode(node, depth, key) {
        const isCollapsed = this.state.collapsedCollections.has(node.id);
        const marginLeft = depth * 24;
        return _jsxs("div", { className: "migration__tree-group", style: { marginLeft }, children: [_jsxs("div", { className: "migration__tree-group__header", children: [_jsx("span", { className: "migration__collection__toggle", onClick: () => this.toggleCollectionCollapse(node.id), children: isCollapsed ? '\u25B6' : '\u25BC' }), _jsx("span", { className: "migration__tree-group__label", onClick: () => this.toggleCollectionCollapse(node.id), children: node.label }), node.openInWorkHub && _jsx("button", { className: "migration__open-workhub", onClick: (e) => { e.stopPropagation(); node.openInWorkHub(); }, children: "Open" })] }), !isCollapsed && node.children.map((child, i) => this.renderTreeNode(child, 0, `${key}-${i}`))] }, key);
    }
    renderEntityNode(diff, depth, key) {
        const { excludedEntityIds, expandedDiffs, rawJsonDiffs } = this.state;
        const isIncluded = !excludedEntityIds.has(diff._id);
        const isExpanded = expandedDiffs.has(diff._id);
        const showRawJson = rawJsonDiffs.has(diff._id);
        const marginLeft = depth * 24;
        const openAction = _entityWorkHubActions.get(diff._id);
        return _jsxs("div", { className: `migration__entity${isIncluded ? '' : ' migration__entity--excluded'}`, style: { marginLeft }, children: [_jsxs("div", { className: "migration__entity__header", onClick: () => this.toggleDiffExpand(diff._id), children: [_jsx("input", { type: "checkbox", checked: isIncluded, onChange: (e) => { e.stopPropagation(); this.toggleEntity(diff._id); }, onClick: (e) => e.stopPropagation() }), _jsx("span", { className: "migration__entity__toggle", children: isExpanded ? '\u25BC' : '\u25B6' }), _jsx("span", { className: "migration__entity__label", children: this.resolveEntityLabel(diff) }), _jsx("span", { className: "migration__entity__id", children: diff._id.slice(0, 8) }), openAction && _jsx("button", { className: "migration__open-workhub", onClick: (e) => { e.stopPropagation(); openAction(); }, children: "Open" }), _jsx("span", { className: "migration__entity__fields", children: diff.changedFields.join(', ') })] }), isExpanded && _jsxs("div", { className: "migration__diff", children: [!showRawJson ? this.renderFieldsTable(diff) : this.renderRawJsonDiff(diff), _jsx("button", { className: "migration__diff__toggle-raw", onClick: () => this.toggleRawJson(diff._id), children: showRawJson ? 'Show summary' : 'Show raw JSON' })] })] }, key);
    }
    renderFieldsTable(diff) {
        const leafDiffs = this.computeLeafDiffs(diff.before, diff.after);
        if (!leafDiffs.length)
            return _jsx("div", { className: "migration__muted", children: "No visible changes (computed fields only)" });
        return _jsxs("table", { className: "migration__fields-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Field" }), _jsx("th", { children: "Before" }), _jsx("th", { children: "After" })] }) }), _jsx("tbody", { children: leafDiffs.map((leaf, i) => (_jsxs("tr", { children: [_jsx("td", { className: "migration__fields-table__field", children: this.formatPath(leaf.path) }), _jsx("td", { className: "migration__fields-table__before", children: this.formatLeafValue(leaf.before) }), _jsx("td", { className: "migration__fields-table__after", children: this.formatLeafValue(leaf.after) })] }, `${leaf.path}-${i}`))) })] });
    }
    renderRawJsonDiff(diff) {
        return _jsxs("div", { className: "migration__diff__panels", children: [_jsxs("div", { className: "migration__diff__panel", children: [_jsx("label", { className: "migration__diff__label migration__diff__label--before", children: "Before" }), _jsx("pre", { className: "migration__diff__pre migration__diff__pre--before", children: JSON.stringify(diff.before, null, 2) })] }), _jsxs("div", { className: "migration__diff__panel", children: [_jsx("label", { className: "migration__diff__label migration__diff__label--after", children: "After" }), _jsx("pre", { className: "migration__diff__pre migration__diff__pre--after", children: JSON.stringify(diff.after, null, 2) })] })] });
    }
    renderResult(result) {
        return _jsxs("div", { className: "migration__card migration__result", children: [_jsxs("div", { className: "migration__result__heading", children: ["Migration complete \u2014 ", result.totalUpdated, " entities updated"] }), result.collectionsUpdated.map(c => (_jsxs("div", { className: "migration__result__row", children: [_jsx("strong", { children: c.collection }), _jsxs("span", { className: "migration__badge--success", children: [c.count, " updated"] })] }, c.collection))), _jsx("hr", { className: "migration__divider" }), _jsx("button", { className: "btn-ghost", onClick: () => this.downloadJson(result.backup, `hv-migration-backup-${result.backup.timestamp}.json`), children: "Download backup" })] });
    }
}
//# sourceMappingURL=Page_Migration.js.map